import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../api';
import { SEGMENTS_CHANGED_EVENT } from './useDocumentSegments';

// Case-wide "documents detected inside compilation files" — the provider behind
// the inventory's *By document* view (plans/BY_DOCUMENT_INVENTORY_VIEW_PROPOSAL.md
// §3.4b, provider 1: virtual items). One GET per case; a ruling made anywhere
// (panel, viewer bands, gallery) fires `dc:segments-changed` and we refetch.
export default function useDetectedDocuments(caseId, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const aliveRef = useRef(true);
  const inflightRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!caseId || !enabled) return null;
    if (inflightRef.current) return inflightRef.current;
    const p = api.get(`/documents/case/${caseId}/detected-documents`)
      .then((d) => { if (aliveRef.current) { setData(d); setError(null); } return d; })
      .catch((e) => { if (aliveRef.current) setError(e?.message || 'Could not load detected documents'); return null; })
      .finally(() => { inflightRef.current = null; });
    inflightRef.current = p;
    return p;
  }, [caseId, enabled]);

  useEffect(() => {
    aliveRef.current = true;
    if (!caseId || !enabled) { setData(null); return undefined; }
    setLoading(true);
    refresh().finally(() => { if (aliveRef.current) setLoading(false); });
    const onChanged = () => { refresh(); };
    window.addEventListener(SEGMENTS_CHANGED_EVENT, onChanged);
    return () => { aliveRef.current = false; window.removeEventListener(SEGMENTS_CHANGED_EVENT, onChanged); };
  }, [caseId, enabled, refresh]);

  // Lookups the gallery needs on every render.
  const filesById = useMemo(() => {
    const m = new Map();
    (data?.files || []).forEach((f) => m.set(f.parent_id, f));
    return m;
  }, [data]);
  const itemsByParent = useMemo(() => {
    const m = new Map();
    (data?.items || []).forEach((it) => {
      if (!m.has(it.parent_id)) m.set(it.parent_id, []);
      m.get(it.parent_id).push(it);
    });
    m.forEach((arr) => arr.sort((a, b) => a.page_start - b.page_start));
    return m;
  }, [data]);

  return { data, loading, error, refresh, filesById, itemsByParent };
}
